<script setup>
import { getGiftingTaxes } from '#common/services/checkoutServices'
import { getCustomerCredits } from '#common/services/customerServices'
import { scrollToElement } from '#common/utils/form'
import ga4 from '#common/utils/ga4'
import { baseURL } from '#common/utils/url'
import { pushEvent, pushPageView } from '#common/utils/user'
import { setErrors } from '@formkit/core'

const { $store, $axios, $auth } = useNuxtApp()
const { showHeader, showFooter } = storeToRefs(useLayout())
const $route = useRoute()
const subtotal = ref(null)
const totalsObject = ref({})
const activeEmailDomainGiftingDiscount = ref({})
const errors = ref([])
const addressErrors = ref({})
const addressFormErrors = ref([])
const order = ref(null)
const orderSuccess = ref(false)
const sections = ref([
  'clubSelection',
  'contactSection',
  'shippingSection',
  'paymentSection',
])
const sectionToState = ref({
  clubSelection: 'giftSelection',
  contactSection: 'contactInformation',
  shippingSection: 'shippingInformation',
  paymentSection: 'paymentInformation',
})
const committingSection = ref('')

const memberships = useMemberships()

const giftableMemberships = computed(() => memberships.giftableMemberships)
await useAsyncData(() => memberships.loadMemberships())
const sectionState = computed(() => memberships.sectionState)
const subscriptionGiftingLoading = computed(
  () => $store.getters.subscriptionGiftingLoading,
)

const metadata = useMetadata()
const stateNamesToCodes = computed(() => metadata.stateNamesToCodes)
const membershipTypeSlug = computed(() => $route.params.slug)
const membershipType = computed(() =>
  giftableMemberships.value.find((membership) => {
    return membership.slug === membershipTypeSlug.value
  }),
)
const committedGift = computed(() =>
  getGift(
    sectionState.value.giftSelection.selectedPlan,
    sectionState.value.giftSelection.selectedGift,
  ),
)
const selectedAddress = computed(() => {
  if (!sectionState.value.shippingInformation.line1) return null
  return {
    line_1: sectionState.value.shippingInformation.line1,
    line_2: sectionState.value.shippingInformation.line2,
    company: sectionState.value.shippingInformation.line3,
    city: sectionState.value.shippingInformation.line4,
    state:
      stateNamesToCodes.value[
        sectionState.value.shippingInformation.state_name
      ],
    postcode: sectionState.value.shippingInformation.postcode,
  }
})
const showPlaceOrderButton = computed(
  () =>
    !sectionState.value.giftSelection.drawerOpen &&
    !sectionState.value.contactInformation.drawerOpen &&
    !sectionState.value.shippingInformation.drawerOpen &&
    !sectionState.value.paymentInformation.drawerOpen,
)

function sectionCommited(sectionId) {
  committingSection.value = sectionId
  let scrolled = false
  for (let i = 0; i < sections.value.length; ++i) {
    const tempSection = sections.value[i]
    if (sectionState.value[sectionToState.value[tempSection]].drawerOpen) {
      scrollToElement(nextTick, `#${tempSection}`)
      scrolled = true
      break
    }
  }
  if (!scrolled) {
    scrollToElement(nextTick, '#checkoutSidebar')
  }
  if (['clubSelection', 'shippingSection'].includes(sectionId)) {
    getTaxes()
  }
}
function getGift(planSlug, giftId) {
  const plan = getPlan(planSlug)
  if (!plan) return null
  return plan.gift_terms.find((gift) => {
    return gift.id === giftId
  })
}
async function getTaxes() {
  if (committedGift.value) {
    const gift = getGift(
      sectionState.value.giftSelection.selectedPlan,
      sectionState.value.giftSelection.selectedGift,
    )
    subtotal.value = gift.base_price
  }
  if (!committedGift.value) {
    totalsObject.value = {}
    errors.value = []
    addressErrors.value = {}
    addressFormErrors.value = []
    return
  }
  memberships.setLoading(true)
  errors.value = []
  addressErrors.value = {}
  addressFormErrors.value = []
  try {
    totalsObject.value = await getGiftingTaxes($axios, {
      term_id: committedGift.value.id,
      ...selectedAddress.value,
    })
  } catch (e) {
    totalsObject.value = {}
    if (e.response && e.response.data) {
      const data = e.response.data
      if (Array.isArray(data)) {
        errors.value = data
      } else if (typeof data === 'object' && data !== null) {
        if (data.state) {
          data.state_name = data.state
        }
        addressErrors.value = data

        addressFormErrors.value = data.non_field_errors || []
        sectionState.value.shippingInformation.drawerOpen = true

        if (committingSection.value === 'shippingSection') {
          for (const key in addressErrors.value) {
            if (key === 'line_1') {
              setErrors(
                'input-giftee-address-line-1',
                `Error: ${addressErrors.value[key]}`,
              )
            } else {
              setErrors(
                `input-giftee-${key}`,
                `Error: ${addressErrors.value[key]}`,
              )
            }
          }
        }
      }
    } else {
      errors.value = ['Error attempting to create new user']
    }
    window.scrollTo(0, 0)
  } finally {
    memberships.setLoading(false)
  }
}
async function getGiftingDiscount() {
  const {
    active_email_domain_gifting_discounts: activeEmailDomainGiftingDiscounts,
  } = await getCustomerCredits($axios)
  if (
    activeEmailDomainGiftingDiscounts &&
    activeEmailDomainGiftingDiscounts.length > 0
  ) {
    activeEmailDomainGiftingDiscount.value =
      activeEmailDomainGiftingDiscounts[0]
  }
}
function getPlan(slug) {
  if (!membershipType.value?.plans) return null
  return membershipType.value.plans.find((plan) => {
    return plan.slug === slug
  })
}

async function placeOrder() {
  memberships.setLoading(true)
  const placeOrderPayload = {
    term_id: committedGift.value.id,
    gift_message: sectionState.value.giftSelection.giftNote,
    send_date: sectionState.value.giftSelection.sendDate,
    gifter_name:
      sectionState.value.contactInformation.firstName +
      ' ' +
      sectionState.value.contactInformation.lastName,
    gifter_email: sectionState.value.contactInformation.email,
    gifter_date_of_birth:
      sectionState.value.contactInformation.gifter_date_of_birth,
    first_name: sectionState.value.shippingInformation.first_name,
    last_name: sectionState.value.shippingInformation.last_name,
    ...selectedAddress.value,
    email: sectionState.value.shippingInformation.email,
    phone: sectionState.value.shippingInformation.phone_number,
    amount: totalsObject.value.grand_total,
    token: sectionState.value.paymentInformation.selectedPaymentMethod,
    new_card:
      !$auth.loggedIn ||
      !sectionState.value.paymentInformation.savePaymentDetails,
    source_url: baseURL() + $route.fullPath,
  }
  let response = null
  errors.value = []
  try {
    response = await $axios.post(
      '/api/membership/gift/checkout/',
      placeOrderPayload,
    )
    order.value = response.data
    orderSuccess.value = true
    pushPageView($axios, {
      name: 'wine-club-gifting-thank-you',
      properties: {
        path: $route.fullPath,
        url: window.location.href,
        title: document.title,
        referrer: document.referrer,
      },
    })
    ga4.track('complete_club_gifting', {
      slug: $route.params.slug,
      orderId: order.value.gift_code,
      totalCost: order.value.grand_total,
      shipmentsSignedUp: order.value.term.num_of_shipments,
      customerId: $auth.user.analytics_id,
      customerEmail: $auth.user.email,
      membershipType: order.value.term.membership_type.name,
      plan: order.value.term.plan.name,
    })
    nextTick(() => window.scrollTo(0, 0))

    errors.value = []
  } catch (e) {
    if (e.response && e.response.data) {
      const data = e.response.data
      if (Array.isArray(data)) {
        errors.value = data
      } else if (typeof data === 'object' && data !== null) {
        if (data.error && data.display_error) {
          if (data.display_error.includes('problem processing your payment')) {
            errors.value.push('Payment charge failed.')
          } else {
            errors.value.push(data.display_error)
          }
        } else {
          Object.keys(data).forEach((key) => {
            errors.value.push(...data[key])
          })
        }
      }
    } else {
      errors.value = ['Error attempting to place order']
    }
  } finally {
    memberships.setLoading(false)
    const errorIndex = errors.value.indexOf('Gift cost has changed')
    if (errorIndex !== -1) {
      // Copy errors so they aren't reset when updating the total
      const errors = [...errors]
      await memberships.loadMemberships()
      await getTaxes()
      errors[errorIndex] =
        'The price of your gift has changed. Please confirm the new total' +
        ' before completing your purchase.'
    }
  }
}
function showHeaderFooter(value) {
  showHeader.value = value
  showFooter.value = value
}

function resetPage() {
  showHeaderFooter(false)
  getTaxes()
  pushEvent(
    {
      metric_name: 'wine club gift checkout started',
      url_path: $route.fullPath,
    },
    $store,
  )
  memberships.clearSectionState()
  if ($auth.loggedIn) {
    getGiftingDiscount()
    memberships.setSectionState({
      section: 'contactInformation',
      value: {
        drawerOpen: false,
        firstName: $auth.user.first_name,
        lastName: $auth.user.last_name,
        email: $auth.user.email,
        gifter_date_of_birth: $auth.user.date_of_birth,
      },
    })
  }
  ga4.track('begin_club_gifting', { slug: $route.params.slug })
}

onActivated(() => {
  resetPage()
})

onDeactivated(() => {
  order.value = null
  orderSuccess.value = false
  totalsObject.value = {}
  errors.value = []
  addressErrors.value = {}
  addressFormErrors.value = []
  memberships.clearSectionState()
  showHeaderFooter(true)
})

onMounted(() => {
  resetPage()
})

onUnmounted(() => {
  showHeaderFooter(true)
})

watch(subscriptionGiftingLoading, () => {
  if (checkoutLoading) {
    checkoutLoading.loading = subscriptionGiftingLoading
  }
})

provide('clubGiftingProvider', { addressErrors, addressFormErrors })

useHead({
  script: [
    {
      src: `https://maps.googleapis.com/maps/api/js?key=${metadata.settingsVars.GOOGLE_MAPS_API_KEY}&libraries=places`,
    },
  ],
})
</script>

<template>
  <div class="surefootcheckout">
    <Loading ref="memberships.loading" />
    <GiftingPurchaseThankYou v-if="orderSuccess" :order="order" />
    <div v-else class="checkout">
      <div class="checkout-header">
        <div class="checkout-header site-header-container simple-header">
          <header id="gifting-header">
            <div class="utility-navigation">
              <div class="left">
                <nuxt-link
                  to="/wine-club/gifting/"
                  @click="showHeaderFooter(true)"
                >
                  Cancel
                </nuxt-link>
              </div>
              <div class="logo">
                <nuxt-link to="/">
                  <span class="sr-only">Wine Access</span>
                </nuxt-link>
              </div>
              <div>
                <!-- Intentional Blank -->
              </div>
            </div>
          </header>
        </div>
      </div>
      <div v-if="giftableMemberships.length > 0" class="checkout-main">
        <div class="row">
          <div class="checkout-details columns small-12 large-8">
            <div class="checkout-details-title row">
              <div>
                <h1>Checkout</h1>
              </div>
            </div>
            <GiftingClubSection
              id="clubSelection"
              ref="clubSelection"
              @commit="sectionCommited('clubSelection')"
            />
            <client-only>
              <GiftingContactSection
                id="contactSection"
                @commit="sectionCommited('contactSection')"
              />
              <GiftingShippingSection
                id="shippingSection"
                @commit="sectionCommited('shippingSection')"
              />
              <GiftingPaymentSection
                id="paymentSection"
                :totals-object="totalsObject"
                @commit="sectionCommited('paymentSection')"
              />
            </client-only>
          </div>
          <div class="checkout-summary columns small-12 large-4">
            <GiftingCheckoutSidebar
              id="checkoutSidebar"
              :subtotal="subtotal"
              :totals-object="totalsObject"
              :active-email-domain-gifting-discount="
                activeEmailDomainGiftingDiscount
              "
              :show-place-order-button="showPlaceOrderButton"
              :errors="errors"
              @placeOrder="placeOrder"
            />
          </div>
        </div>
      </div>
      <div class="checkout-footer">
        <div class="checkout-footer-content">
          <div class="row">
            <div class="logo columns small-12 medium-6">
              <nuxt-link to="/">
                <span class="sr-only">Wine Access</span>
              </nuxt-link>
            </div>
            <div class="links columns small-12 medium-6">
              <ul>
                <li>
                  <nuxt-link to="/where-we-ship/">Where We Ship</nuxt-link>
                </li>
                <li>
                  <nuxt-link to="/contact-us/">Help</nuxt-link>
                </li>
                <li>
                  <nuxt-link to="/contact-us/">FAQs</nuxt-link>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
